import React, { useState } from 'react';
import { Button, Typography, Modal, TextArea } from '@scale/scaleui';
import { ResumeScreeningJob } from '@scale/shared-types';
import { triggerOneOffScreeningJob } from 'react-scale/corp/lib/api/growth';
import { isValidEmail } from 'react-scale/components/design-system/ObjectIdInput/ObjectIdInput';
import SAlert from 'react-s-alert';

import moment from 'moment';

const RunOneOffScreeningJobModal: React.FC<{
  open: boolean;
  onClose: () => void;
  jobConfig: Partial<ResumeScreeningJob>;
  handleLastRunAtChange: (lastRunAt: Date) => void;
}> = ({ open, onClose, jobConfig, handleLastRunAtChange }) => {
  const [emails, setEmails] = useState<string>('');
  const onEmailInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEmails(e.target.value);
  };

  const shouldDisableRunButton = () => {
    return (
      !emails ||
      !jobConfig._id ||
      (jobConfig.lastRunAt && moment().diff(moment(jobConfig.lastRunAt), 'minutes') < 30)
    );
  };

  const startJob = async () => {
    if (!jobConfig._id) {
      SAlert.error('Job configuration loading failed. Please try again later.');
      onClose();
      return;
    }
    const recipients: string[] = emails
      .split(/[\s,;\r\n]+/)
      .map(email => email.trim())
      .filter(email => isValidEmail(email));
    const uniqueRecipients = Array.from(new Set(recipients));
    if (uniqueRecipients.length === 0) {
      SAlert.error('Please enter valid email addresses.');
      return;
    }
    if (uniqueRecipients.length > 5000) {
      SAlert.error('Please enter less than 5000 email addresses.');
      return;
    }
    try {
      await triggerOneOffScreeningJob(uniqueRecipients, jobConfig._id);
      handleLastRunAtChange(new Date());
      SAlert.success(
        `Your screening job for ${uniqueRecipients.length} emails is underway. Results will arrive shortly via email. If the job fails, you'll be notified. Please avoid resubmitting for 24 hours.`,
        {
          timeout: 30000,
        },
      );
      onClose();
    } catch (error) {
      SAlert.error('Failed to start the screening job.', error);
    }
  };

  return (
    <Modal
      innerClassName="scaleui scale-tailwind scale-theme w-[60%] h-[80%]"
      open={open}
      onClose={onClose}
      title="Run One-Off Screening Job"
      actions={
        <div className="flex flex-row-reverse gap-3 ">
          <Button
            key="confirm"
            onClick={startJob}
            className="px-2"
            disabled={shouldDisableRunButton()}
          >
            Run One-Off Screening Job
          </Button>
          <Button key="cancel" onClick={onClose} variant="neutral" className="px-2">
            Cancel
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4 h-full">
        <div>
          <Typography.Subtitle2>Input Contributor emails</Typography.Subtitle2>
          <Typography.Paragraph2>
            Enter a list of emails and we will pull in their resumes from Corp. Enter up to 5000
            emails.
          </Typography.Paragraph2>
        </div>
        <TextArea
          label=""
          dynamic
          value={emails}
          onChange={onEmailInputChange}
          placeholder="Enter emails separated by commas or newlines"
        />
        <Typography.Paragraph2>
          Next run will be available at:{' '}
          {jobConfig.lastRunAt && moment().diff(moment(jobConfig.lastRunAt), 'minutes') < 30
            ? moment(jobConfig.lastRunAt).add(30, 'minute').format('LT')
            : 'now'}
        </Typography.Paragraph2>
      </div>
    </Modal>
  );
};

export default RunOneOffScreeningJobModal;
